import React, { useState, useEffect } from 'react';
import { newsService } from '../../services/newsService';
import { imageService } from '../../services/imageService';
import { resolveAssetUrl } from '../../services/api';

const EMPTY_FORM = {
  key: '',
  sort_order: 0,
  category: '',
  title: '',
  lead: '',
  content: '',
  published_date: new Date().toISOString().slice(0, 10),
  read_minutes: 3,
  is_active: true,
  cover_url: ''
};

function slugify(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function NewsManager() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [coverUploading, setCoverUploading] = useState(false);

  const loadNews = () => {
    setLoading(true);
    newsService
      .getAllNewsAdmin()
      .then((res) => {
        if (res.data) setNews(res.data);
      })
      .catch((err) => alert(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadNews();
  }, []);

  const handleOpenAdd = () => {
    setEditingItem(null);
    setFormData({
      ...EMPTY_FORM,
      sort_order: news.length ? Math.max(...news.map((n) => n.sort_order || 0)) + 1 : 1
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setFormData({
      key: item.key || '',
      sort_order: item.sort_order ?? 0,
      category: item.category || '',
      title: item.title || '',
      lead: item.lead || '',
      content: item.content || '',
      published_date: item.published_date ? String(item.published_date).slice(0, 10) : '',
      read_minutes: item.read_minutes || 3,
      is_active: item.is_active ?? true,
      cover_url: item.cover_url || ''
    });
    setModalOpen(true);
  };

  const handleDelete = async (id, title) => {
    if (window.confirm(`Xoá bài viết "${title}"?\n\nHành động không thể hoàn tác.`)) {
      try {
        await newsService.deleteNews(id);
        loadNews();
      } catch (err) {
        alert(err.message);
      }
    }
  };

  const handleMove = async (item, dir) => {
    const idx = news.findIndex((n) => n.id === item.id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= news.length) return;
    const other = news[swapIdx];
    try {
      await Promise.all([
        newsService.updateNews(item.id, { sort_order: other.sort_order }),
        newsService.updateNews(other.id, { sort_order: item.sort_order })
      ]);
      loadNews();
    } catch (err) {
      alert('Lỗi đổi thứ tự: ' + err.message);
    }
  };

  const handleCoverPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('File quá lớn (>5MB)');
      return;
    }
    const fd = new FormData();
    fd.append('image', file);
    setCoverUploading(true);
    try {
      const res = await imageService.upload(fd);
      setFormData((prev) => ({ ...prev, cover_url: res.data.url }));
    } catch (err) {
      alert('Lỗi upload: ' + err.message);
    } finally {
      setCoverUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const keyStr = formData.key.trim() || slugify(formData.title) || ('bai-viet-' + Date.now().toString(36));
    if (!/^[a-z0-9-]+$/.test(keyStr)) {
      alert('Slug chỉ được dùng chữ thường, số và gạch ngang.');
      return;
    }
    const payload = {
      ...formData,
      key: keyStr,
      sort_order: Number(formData.sort_order) || 0,
      read_minutes: Number(formData.read_minutes) || 3
    };
    try {
      if (editingItem) {
        await newsService.updateNews(editingItem.id, payload);
      } else {
        await newsService.createNews(payload);
      }
      setModalOpen(false);
      loadNews();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <section className="admin-panel">
      <div className="panel-head">
        <div>
          <h2>Tin tức &amp; Sự kiện</h2>
          <p className="panel-head__sub">
            Quản lý tin bài hiển thị ở mục <strong>Cập nhật từ Thế Thảo</strong> (trang chủ) và toàn bộ trang <code>/tin-tuc</code>.
          </p>
        </div>
        <button className="btn btn--primary" onClick={handleOpenAdd} type="button">
          + Thêm bài viết
        </button>
      </div>

      {loading ? (
        <p className="muted">Đang tải…</p>
      ) : news.length === 0 ? (
        <p className="muted">Chưa có bài viết nào. Bấm <strong>+ Thêm bài viết</strong> để tạo mới.</p>
      ) : (
        <div className="jobs-list">
          {news.map((n, i) => (
            <div key={n.id} className={`job-row ${n.is_active ? '' : 'is-inactive'}`}>
              <div className="job-row__main">
                <span className="job-row__eyebrow">
                  {n.category || ''} · {n.published_date ? String(n.published_date).slice(0, 10) : ''}
                </span>
                <span className="job-row__title">
                  {n.title}
                  <span className={`job-row__badge ${n.is_active ? '' : 'job-row__badge--off'}`}>
                    {n.is_active ? 'Đang hiện' : 'Đã ẩn'}
                  </span>
                </span>
                <span className="job-row__meta">
                  key: <code>{n.key}</code> · sort: {n.sort_order} · {n.read_minutes || 3}p đọc
                  {n.cover_url ? <span style={{ color: 'var(--c-green-700)' }}> · có cover</span> : ' · gradient'}
                </span>
              </div>
              <div className="job-row__actions">
                <button className="icon-btn" type="button" disabled={i === 0} title="Lên" onClick={() => handleMove(n, -1)}>↑</button>
                <button className="icon-btn" type="button" disabled={i === news.length - 1} title="Xuống" onClick={() => handleMove(n, 1)}>↓</button>
                <button className="icon-btn" type="button" title="Sửa" onClick={() => handleOpenEdit(n)}>✎</button>
                <button className="icon-btn icon-btn--danger" type="button" title="Xoá" onClick={() => handleDelete(n.id, n.title)}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <div className="admin-modal">
          <div className="admin-modal__backdrop" onClick={() => setModalOpen(false)} />
          <div className="admin-modal__panel" role="dialog" aria-modal="true">
            <button className="admin-modal__close" aria-label="Đóng" type="button" onClick={() => setModalOpen(false)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
            <div className="admin-modal__body">
              <h2>{editingItem ? 'Sửa bài viết: ' + editingItem.title : 'Thêm bài viết mới'}</h2>
              <form onSubmit={handleSubmit} className="admin-form">
                <div className="field-row">
                  <label className="field">
                    <span className="field__label">Slug (URL key)</span>
                    <input
                      type="text"
                      value={formData.key}
                      onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                      pattern="[a-z0-9\-]+"
                      placeholder="tự sinh từ tiêu đề"
                    />
                    <small className="hint">Chỉ chữ thường, số, gạch ngang. Để trống = tự sinh.</small>
                  </label>
                  <label className="field">
                    <span className="field__label">Sort order</span>
                    <input
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                    />
                    <small className="hint">Số nhỏ hiển thị trước. Trang chủ lấy 3 bài đầu.</small>
                  </label>
                </div>

                <label className="field">
                  <span className="field__label">Danh mục (eyebrow) <em>*</em></span>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="vd: Pháp lý dược, Hoạt động, Xu hướng"
                    required
                  />
                </label>

                <label className="field">
                  <span className="field__label">Tiêu đề bài viết <em>*</em></span>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="vd: Cập nhật quy định mới về kiểm soát TPCN năm 2026"
                    required
                  />
                </label>

                <label className="field">
                  <span className="field__label">Lead — mô tả ngắn <em>*</em></span>
                  <textarea
                    rows="3"
                    value={formData.lead}
                    onChange={(e) => setFormData({ ...formData, lead: e.target.value })}
                    placeholder="1-2 câu tóm tắt bài viết, hiển thị ở card."
                    required
                  />
                </label>

                <label className="field">
                  <span className="field__label">Nội dung chi tiết</span>
                  <textarea
                    rows="6"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Nội dung bài viết đầy đủ..."
                  />
                </label>

                <div className="field-row">
                  <label className="field">
                    <span className="field__label">Ngày đăng <em>*</em></span>
                    <input
                      type="date"
                      value={formData.published_date}
                      onChange={(e) => setFormData({ ...formData, published_date: e.target.value })}
                      required
                    />
                  </label>
                  <label className="field">
                    <span className="field__label">Số phút đọc</span>
                    <input
                      type="number"
                      min="1"
                      max="60"
                      value={formData.read_minutes}
                      onChange={(e) => setFormData({ ...formData, read_minutes: e.target.value })}
                    />
                  </label>
                  <label className="field field--toggle">
                    <span className="field__label">Hiển thị public?</span>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      />
                      <span></span>
                    </label>
                  </label>
                </div>

                <div className="field">
                  <span className="field__label">Ảnh cover <small>(16:9 · auto lưu khi chọn ảnh)</small></span>
                  <div className="cover-picker">
                    <div className="cover-preview">
                      {formData.cover_url ? (
                        <img src={resolveAssetUrl(formData.cover_url)} alt="cover preview" />
                      ) : (
                        <span className="cover-preview__hint">Chưa có ảnh — dùng gradient mặc định</span>
                      )}
                    </div>
                    <div className="cover-picker__actions">
                      <label className="btn btn--ghost" style={{ cursor: coverUploading ? 'wait' : 'pointer' }}>
                        {coverUploading ? 'Đang upload…' : 'Chọn ảnh…'}
                        <input type="file" accept="image/jpeg,image/png,image/webp" hidden disabled={coverUploading} onChange={handleCoverPick} />
                      </label>
                      {formData.cover_url && (
                        <button type="button" className="btn btn--ghost" onClick={() => setFormData({ ...formData, cover_url: '' })}>
                          Bỏ ảnh
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn--ghost" onClick={() => setModalOpen(false)}>Huỷ</button>
                  <button type="submit" className="btn btn--primary">Lưu</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
